/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import {
	type ExtensionAPI,
	type ExtensionManifest,
	type ExtensionProvider,
	type FieldDefinition,
} from '@atlaskit/editor-common/extensions';
import type { EditorActions } from '@atlaskit/editor-core';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { ForgeExtensionProvider, type SendAnalyticsFunc } from '../forgeExtensionProvider';
import { shouldBlockExtensionDueToAppAccessPolicy } from '../../render';
import { parse } from '@atlassian/cs-ari';
import { getExtensionType, isCustomUI as getIsCustomUI } from '@atlassian/forge-ui';
import type { GQLExtensionContextsFilter } from '@atlassian/forge-ui/provider';
import { getForgeUIExtensionsAsync } from '@atlassian/forge-ui/provider';

import {
	type AnalyticsWebClient,
	extensionIdToAnalyticsAttributes,
	type ForgeUIAnalyticsContext,
	sendEvent,
} from '@atlassian/forge-ui/analytics';
import { type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import type ApolloClient from 'apollo-client';
import { css, jsx } from '@compiled/react';
import type { Node } from 'prosemirror-model';
import {
	CONNECT_ADDON_ACTION_SUBJECT,
	EXTENSION_NAMESPACE,
	MODULE_NAME,
} from '../../utils/constants';
import { CONFIG_USER_PICKER_PROVIDER } from '../../config/createConfigSchema';
import { createTitleWithEnvironmentInfo } from '../getForgeExtensionProvider';
import { createLocalId } from '../../utils/createLocalId';
import { extractKeyFromId } from '../../utils/extractKeyFromId';
import Icon from '../icon';
import {
	type ForgeConfigPayload,
	validateForgeConfigPayload,
	handleNodeUpdateError,
} from '../customConfigValidator';
import { maybeUpgradeConnectExtensionToForge } from './upgradeConnectNodeToForge';
import {
	type CustomEditContext,
	type ForgeCreateExtensionProps,
	type ForgeExtension,
	type ForgeExtensionManifest,
	type ForgeExtensionParameters,
} from '../../types';
import { createConnectToForgeGetFieldsDefinitionFunction } from '../../config/getFieldsDefinition';

const manifestIcons = {
	'16': () => import('../icon'),
	'24': () => import('../icon'),
	'48': () => import('../icon'),
};

// We keep track of the reason that an extension failed to upgrade, for analytics purposes
const connectToForgeBlockedExtensions = new Set();

const transformConfigBefore = (parameters: ForgeExtensionParameters) => getConfig(parameters);
const transformConfigAfter = (parameters: any) => ({ guestParams: parameters });

const getConfig = ({ config, guestParams }: ForgeExtensionParameters) => guestParams || config;

const bodiedExtensionHelper: {
	setRenderer: (renderer: (() => JSX.Element) | null) => void;
} = {
	setRenderer: () => {},
};

const hideIframe = css({
	display: 'none',
});

/**
 * A component that allows us to render Forge UI Extensions directly in the editor
 * Normally, they are rendered as part of the extension manifest's render function,
 * but that function is not called for bodied extensions, so we need to render them separately.
 */
export const EditorForgeExtensionConfigHelperRenderer = () => {
	const [renderer, setRenderer] = React.useState<(() => JSX.Element) | null>(null);
	bodiedExtensionHelper.setRenderer = (renderer) =>
		setRenderer(
			// We wrap the renderer function here, otherwise setState thinks we're passing a state mutation function
			() => renderer,
		);

	return renderer ? renderer() : null;
};

const mergeNodeWithPayload = <T extends Record<string, unknown>>(
	attrs: T,
	{ config, body }: ForgeConfigPayload,
	allowBody: boolean,
): { attrs: T } & Pick<ForgeCreateExtensionProps, 'content'> => {
	return {
		attrs: {
			...attrs,
			parameters: {
				...(attrs.parameters || {}),
				...transformConfigAfter(config),
			},
		},
		...(body && allowBody ? { content: body.content } : {}),
	};
};

export interface ForgeExtensionProviderSharedParams {
	accountId?: string;
	apolloClient: ApolloClient<object>;
	cloudId: string;
	contextIds: string[];
	forgeUIAnalyticsContext: ForgeUIAnalyticsContext;
	page: string;
	product: string;
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient> | undefined;
	createRenderFunction: (
		extension?: ForgeUIExtensionType,
		customEditContext?: CustomEditContext,
	) => Promise<({ node }: { node: ForgeExtension }) => JSX.Element>;
	connectExtensionProvider?: Promise<ExtensionProvider>;
	editorActions?: EditorActions;
	getFieldsDefinitionFunction: (data: ForgeExtensionParameters) => Promise<FieldDefinition[]>;
	extensionsFilter?: GQLExtensionContextsFilter[];
	dataClassificationTagIds?: string[];
	locale?: string;
}

export async function getForgeExtensionProviderSharedRefactor({
	accountId,
	apolloClient,
	cloudId,
	contextIds,
	forgeUIAnalyticsContext,
	page,
	product,
	analyticsWebClient,
	createRenderFunction,
	getFieldsDefinitionFunction,
	connectExtensionProvider,
	editorActions,
	extensionsFilter,
	dataClassificationTagIds,
	locale,
}: ForgeExtensionProviderSharedParams) {
	let extensionProvider: ForgeExtensionProvider;
	const { extensions } = await getForgeUIExtensionsAsync({
		client: apolloClient,
		contextIds,
		moduleType: MODULE_NAME,
		expandAppOwner: true,
		extensionsFilter,
		dataClassificationTagIds,
		locale,
	});

	const eventSource = editorActions ? 'editPageScreen' : 'viewPageScreen';

	const updateFunction = async (
		parameters: ForgeExtensionParameters,
		actions?: ExtensionAPI<ForgeExtensionParameters>,
		extension?: ForgeUIExtensionType,
		hasCustomConfig?: boolean,
	): Promise<ForgeExtensionParameters | void> => {
		await maybeUpgradeConnectExtensionToForge({
			actions,
			analyticsWebClient,
			connectToForgeBlockedExtensions,
			editorActions,
			eventSource,
			extensionProvider,
			forgeUIAnalyticsContext,
		});

		const node = editorActions?.getSelectedNode();

		const isBodiedExtension = node?.type.name === 'bodiedExtension';
		if (node && extension && actions && (hasCustomConfig || isBodiedExtension)) {
			const forgeExtensionNode = convertNodeToForgeExtension(node);

			// No need to wait for config to render to start showing the panel (for non-custom config)
			// noinspection ES6MissingAwait
			renderConfigHelper({
				extension,
				forgeExtensionNode,
				hasCustomConfig: Boolean(hasCustomConfig),
				isInitialInsertion: false,
				onSubmit: (payload) => updateNodeWithPayload(actions, node.attrs.localId, payload),
				onValidate: (data) =>
					validateForgeConfigPayload(data, {
						isBodiedExtension,
						isInitialInsertion: false,
						schema: editorActions?._privateGetEditorView?.()?.state.schema,
						useStrictParameterValidation: fg('enable-strict-forge-macro-parameter-validation'),
					}),
			});
		}

		if (!hasCustomConfig) {
			return actions!.editInContextPanel(transformConfigBefore, async (params) =>
				transformConfigAfter(params),
			);
		}
	};

	const updateNodeWithPayload = (
		actions: ExtensionAPI,
		localId: string,
		payload: ForgeConfigPayload,
	) => {
		try {
			actions.doc.update(localId, (currentValue) =>
				mergeNodeWithPayload(
					currentValue?.attrs || {},
					payload,
					fg('forge_macro_allow_edit_body_after_insert'),
				),
			);
		} catch (error: unknown) {
			handleNodeUpdateError(error);
		}
	};

	/**
	 * Converts a ProseMirror Node (received by EditorActions API) to a ForgeExtension (provided to render function)
	 * This includes:
	 * - Extracting the node's attributes
	 * - Extracting the node's type
	 * - Transforming the node's content from ProseMirror to ADF JSON (and handling any errors)
	 */
	const convertNodeToForgeExtension = (node: Node): ForgeExtension => {
		const forgeExtension = {
			...node.attrs,
			type: node.type.name,
		} as ForgeExtension;
		try {
			const { content } = new JSONTransformer().encode(node);
			return { ...forgeExtension, content };
		} catch (error: unknown) {
			const errorMessage =
				error instanceof Error
					? error.message
					: typeof error === 'string'
						? error
						: JSON.stringify(error);

			// failed to encode node to JSON
			// continue rendering custom config without content and emit analytics error event
			if (analyticsWebClient) {
				const { extensionKey, localId, environmentType } = node?.attrs;

				sendEvent(analyticsWebClient)({
					source: 'editPageScreen',
					...forgeUIAnalyticsContext,
					eventType: OPERATIONAL_EVENT_TYPE,
					action: 'failed',
					actionSubject: 'forge.ui.extension',
					attributes: {
						method: 'convertNodeToForgeExtension',
						errorMessage,
						localId,
						extensionKey,
						forgeEnvironment: environmentType,
					},
				});
			}
			return forgeExtension;
		}
	};

	const renderConfigHelper = async ({
		extension,
		forgeExtensionNode,
		hasCustomConfig,
		isInitialInsertion,
		onSubmit,
		onValidate,
	}: {
		extension: ForgeUIExtensionType;
		forgeExtensionNode: ForgeExtension;
		hasCustomConfig: boolean;
		isInitialInsertion: boolean;
		onSubmit: (payload: ForgeConfigPayload) => void;
		onValidate: (data: unknown) => ForgeConfigPayload;
	}) => {
		let gqlExtension: ForgeUIExtensionType = extension;
		let customEditContext: CustomEditContext | undefined;

		if (hasCustomConfig) {
			gqlExtension = {
				...extension,
				properties: {
					...extension.properties,
					resource: extension.properties?.config?.resource,
					render: extension.properties?.config?.render,
					title: extension.properties?.config?.title,
					viewportSize: extension.properties?.config?.viewportSize,
				},
			};

			const onModalClose = () => bodiedExtensionHelper.setRenderer(null);
			customEditContext = {
				isInserting: isInitialInsertion,
				bridge: {
					close: onModalClose,
					submit: ({ data }) => {
						// This can throw and propagate exceptions back down to the app
						const payload = onValidate(data);

						// This can throw and propagate exceptions back down to the app
						onSubmit(payload);

						if (!payload.keepEditing) {
							onModalClose();
						}
						return true;
					},
				},
				modalExtension: {
					type: 'macroConfig',
					title: extension.properties?.config?.title,
					closeModalExtension: onModalClose,
					modalWidth: extension.properties?.config?.viewportSize,
					shouldCloseOnOverlayClick: false,
					shouldCloseOnEscapePress: false,
				},
			};
		}

		const renderFunction = await createRenderFunction(gqlExtension, customEditContext);

		// The Forge Doc config iframe should be hidden and resolve the config in the background
		// The loading component for custom config is unaffected as it is rendered in a Portal
		bodiedExtensionHelper.setRenderer(() => (
			<div css={hideIframe}>{renderFunction({ node: forgeExtensionNode })}</div>
		));
	};

	const sendConnectToForgeResolveAnalytics: SendAnalyticsFunc = (extensionKey, errorMessage) => {
		if (analyticsWebClient) {
			sendEvent(analyticsWebClient)({
				source: eventSource,
				...forgeUIAnalyticsContext,
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'h11n_macro_resolve_failed',
				actionSubject: CONNECT_ADDON_ACTION_SUBJECT,
				actionSubjectId: extensionKey,
				attributes: {
					errorMessage,
					extensionKey,
				},
			});
		}
	};

	const fieldsModules: ExtensionManifest<ForgeExtensionParameters>['modules']['fields'] = {
		user: {
			[CONFIG_USER_PICKER_PROVIDER]: {
				provider: async () => ({
					siteId: cloudId,
					// If principalId === 'Context', upstream attempts to use other context to discover the principal ID
					principalId: accountId || 'Context',
					fieldId: 'forgeConfigUserPicker',
					productKey: product as 'confluence',
				}),
			},
		},
	};

	const extensionManifests: ForgeExtensionManifest[] = (extensions || [])
		.filter(({ id }) => parse(id).resourceId !== undefined)
		.map((extension) => {
			const { id, properties, environmentType, migrationKey, environmentKey } = extension;
			const title = createTitleWithEnvironmentInfo(
				properties.title || 'Forge UI Macro',
				environmentType,
				environmentKey,
			);

			const extensionIdWithoutPrefix = parse(id).resourceId!;
			const isCustomUI = getIsCustomUI(extension);
			const isUiKitTwo = extension.properties.render === 'native';

			const hasUiKitOneConfig = !!properties.config && !!properties.config.function;
			const hasConfig = isUiKitTwo || isCustomUI ? !!properties.config : hasUiKitOneConfig;
			const hasCustomConfig = (isUiKitTwo || isCustomUI) && !!properties.config?.resource;

			const connectModuleKey = extractKeyFromId(extensionIdWithoutPrefix);
			const createExtensionProps = (macroId?: string): ForgeCreateExtensionProps => {
				const localId = macroId || createLocalId();
				const extensionId = id;
				const type = getExtensionType(extension);
				return {
					type: type,
					attrs: {
						extensionKey: extensionIdWithoutPrefix,
						extensionType: EXTENSION_NAMESPACE,
						parameters: {
							localId,
							extensionId,
							extensionTitle: title,
						},
						text: title,
						localId,
					},
					...(type === 'bodiedExtension'
						? {
								// include empty content so bodiedExtension doesn't throw error
								content: [{ type: 'paragraph', content: [] }],
							}
						: {}),
				};
			};

			const icon = <Icon label={properties?.title ?? ''} imgUrl={properties.icon} />;
			const macroIcons =
				properties.icon && properties.icon.length > 0
					? {
							'16': () => Promise.resolve(() => icon),
							'24': () => Promise.resolve(() => icon),
							'48': () => Promise.resolve(() => icon),
						}
					: manifestIcons;

			const getFieldsDefinition = createConnectToForgeGetFieldsDefinitionFunction({
				analyticsWebClient,
				connectToForgeBlockedExtensions,
				eventSource,
				extension,
				forgeUIAnalyticsContext,
				hasCustomConfig,
				hasUiKitOneConfig,
				isCustomUI,
				isUiKitTwo,
				uiKitOneGetFieldsDefinitionFunction: getFieldsDefinitionFunction,
			});

			return {
				title,
				description: properties.description,
				type: EXTENSION_NAMESPACE,
				key: extensionIdWithoutPrefix,
				icons: macroIcons,
				categories: properties.categories,
				connectModuleKey,
				migrationKey,
				createExtensionProps,
				modules: {
					...(shouldBlockExtensionDueToAppAccessPolicy(extension)
						? {}
						: {
								quickInsert: [
									{
										key: 'forge-macro',
										action: async (actions) => {
											let extensionProps = createExtensionProps();
											const { localId, extensionId } = extensionProps.attrs.parameters;
											const { type: extensionType } = extensionProps;
											const isBodiedExtension = extensionType === 'bodiedExtension';

											if (hasCustomConfig) {
												const forgeExtensionNode = {
													...extensionProps.attrs,
													type: extensionType,
												};
												const payload = await new Promise<ForgeConfigPayload>((resolve) => {
													let isInitialInsertion = true;
													renderConfigHelper({
														extension,
														forgeExtensionNode,
														hasCustomConfig,
														isInitialInsertion: true, // We don't use the variable here because this is only executed once
														onSubmit: (payload) => {
															if (isInitialInsertion) {
																isInitialInsertion = false;
																resolve(payload);
															} else if (actions) {
																// Update existing node
																updateNodeWithPayload(actions, localId, payload);
															}
														},
														onValidate: (data) =>
															validateForgeConfigPayload(data, {
																isBodiedExtension,
																isInitialInsertion,
																schema: editorActions?._privateGetEditorView?.()?.state.schema,
															}),
													});
												});
												extensionProps = {
													...extensionProps,
													...mergeNodeWithPayload(extensionProps.attrs, payload, true),
												};
											}

											if (analyticsWebClient) {
												sendEvent(analyticsWebClient)({
													source: eventSource,
													eventType: OPERATIONAL_EVENT_TYPE,
													action: 'inserted',
													actionSubject: 'forge.ui.extension',
													...forgeUIAnalyticsContext,
													attributes: {
														localId,
														...extensionIdToAnalyticsAttributes(extensionId),
														extensionType,
														forgeEnvironment: extension.environmentType,
													},
												});
											}

											return extensionProps;
										},
									},
								],
							}),
					nodes: {
						default: {
							type: 'extension',
							render: () => createRenderFunction(extension),
							update: hasConfig
								? (params, actions) => updateFunction(params, actions, extension, hasCustomConfig)
								: undefined,
							getFieldsDefinition,
						},
					},
					fields: fieldsModules,
				},
			};
		});

	const legacyMacroManifest: ExtensionManifest<ForgeExtensionParameters> = {
		/**
		 * title, description and icons are required but will not be used anywhere,
		 * since this manifest is used solely for rendering legacy nodes
		 **/
		title: 'Forge UI Macro',
		description: 'Supports existing Forge UI Macro nodes',
		type: EXTENSION_NAMESPACE,
		key: 'xen:macro',
		icons: manifestIcons,
		modules: {
			nodes: {
				default: {
					type: 'extension',
					render: createRenderFunction,
					update: updateFunction,
					getFieldsDefinition: getFieldsDefinitionFunction,
				},
			},
			fields: fieldsModules,
		},
	};

	const manifests = [legacyMacroManifest, ...extensionManifests];
	extensionProvider = new ForgeExtensionProvider(
		manifests,
		connectExtensionProvider,
		sendConnectToForgeResolveAnalytics,
	);
	return extensionProvider;
}
